import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import GMark from "@/components/GMark";
import Seo from "@/components/Seo";
import PricingSection from "@/components/PricingSection";
import FooterMeta from "@/components/FooterMeta";
import { breadcrumb } from "@/lib/schema";

/**
 * Standalone /pricing page — deep-linkable, no login required. Content is
 * driven by the shared <PricingSection/> which fetches /api/plans, so the
 * backend PLANS dict is the single source of truth for prices, product
 * allowances, feature lists and CTAs.
 */
export default function Pricing() {
  return (
    <div className="min-h-screen bg-[#F5F0E5] text-[#20251F]">
      <Seo
        title="Pricing — flat monthly subscription, no commission"
        description="Gate + Gather pricing: try free for 14 days on the Honesty Box, then move up to Gate for $20 NZD per month. Flat monthly subscription — we don't take a percentage of your sales. Standard payment-provider fees may apply if you accept online card payments."
        path="/pricing"
        jsonLd={[
          breadcrumb([
            { name: "Gate + Gather", path: "/" },
            { name: "Pricing", path: "/pricing" },
          ]),
        ]}
      />

      <header className="border-b border-[#DED5C3] bg-[#F5F0E5]/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="pricing-nav-brand">
            <GMark size={30} className="rounded-lg shrink-0" />
            <span className="font-serif-heading text-xl tracking-tight">Gate<span className="brand-plus text-[1.05em]"> + </span>Gather</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/for-growers" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-3 py-2 hidden sm:inline" data-testid="pricing-nav-forgrowers">For growers</Link>
            <Link to="/faq" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-3 py-2 hidden sm:inline" data-testid="pricing-nav-faq">FAQ</Link>
            <Link to="/seller/login" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-3 py-2" data-testid="pricing-nav-login">Log in</Link>
            <Link to="/seller/signup" data-testid="pricing-nav-signup">
              <Button className="bg-[#20251F] hover:bg-black rounded-full text-white">Open your Gate</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-4">
        <p className="font-mono-label text-sm uppercase tracking-[0.18em] text-[#3F674E]/85 mb-4" data-testid="pricing-hero-eyebrow">
          One plan, one price, no commission
        </p>
        <h1 className="font-serif-heading text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#20251F]" data-testid="pricing-hero-h1">
          Two simple monthly plans. Your sales stay yours.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-stone-700 max-w-2xl leading-relaxed">
          Pick the plan that fits the range you sell. Both plans give you your own Gate storefront, orders, stock tracking, pickup and delivery, a Notify Me list and a shareable link.
        </p>
        <p className="mt-4 text-base sm:text-lg text-stone-700 max-w-2xl leading-relaxed">
          Gate + Gather is designed for independent growers running small, changing inventories — not enormous ecommerce catalogues. Instead of taking a percentage of every sale, we charge a flat monthly subscription so your sales stay yours. Prices are shown in New Zealand dollars.
        </p>
      </section>

      <PricingSection variant="page" eyebrow="Plans" heading="Choose your Gate." />

      {/* Long-form comparison + billing detail — added to lift the page above
          "very short content" so search engines see substantive body copy on
          the pricing route, not just three cards and a footer. */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 border-t border-[#DED5C3]" data-testid="pricing-longform">
        <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80 mb-3">How the plans compare</p>
        <h2 className="font-serif-heading text-3xl sm:text-4xl text-[#20251F] tracking-tight leading-tight">
          Start small. Move up when you're ready.
        </h2>
        <div className="mt-8 grid md:grid-cols-2 gap-6 sm:gap-8">
          <div>
            <p className="font-serif-heading text-xl text-[#20251F]">Honesty Box — try it free for 14 days</p>
            <p className="mt-3 text-[15px] leading-relaxed text-stone-700">
              The Honesty Box is a two-week free trial with room for one live product. It's the quickest way to see how a Gate feels in your own hands — list your first plant, share the link with a couple of regulars, and see whether the six questions your customers keep asking really do answer themselves.
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-stone-700">
              No card required to start. No commission taken on any sale. Your customers stay yours the whole time.
            </p>
          </div>
          <div>
            <p className="font-serif-heading text-xl text-[#20251F]">Gate — $20 NZD per month</p>
            <p className="mt-3 text-[15px] leading-relaxed text-stone-700">
              Gate is our core plan for independent growers who want a full storefront. You can list up to 25 new products in each monthly cycle, take online card payments and offline (cash or bank) payments side by side, manage pickup and local delivery, run a Notify Me waitlist, and share one clean link across Instagram, Facebook and your printed signage.
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-stone-700">
              Billing is a flat monthly subscription. Nothing is deducted from your sales. If you accept online card payments, standard payment-provider fees still apply — those go to the payment processor, not to us.
            </p>
          </div>
        </div>

        <div className="mt-10 rounded-3xl border border-[#DED5C3] bg-[#FFFCF6] p-6 sm:p-8" data-testid="pricing-what-included">
          <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E]">What's included on every plan</p>
          <p className="mt-2 font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight leading-tight">Your Gate, your customers, your sales.</p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[15px] leading-relaxed text-stone-700 list-disc pl-5 marker:text-[#3F674E]">
            <li>Your own shareable storefront link at gateandgather.co.nz/g/yourname</li>
            <li>Live availability and prices — updated in a few taps</li>
            <li>Online card payments and offline payment side by side</li>
            <li>Pickup, local delivery or both — you decide</li>
            <li>Notify Me waitlist for sold-out plants</li>
            <li>Order confirmations by email</li>
            <li>Simple stock adjustments after markets</li>
            <li>A QR code for your gate, stall or signage</li>
          </ul>
        </div>

        <div className="mt-10 space-y-6" data-testid="pricing-faq-short">
          <div>
            <p className="font-serif-heading text-lg text-[#20251F]">Does Gate + Gather take a percentage of my sales?</p>
            <p className="mt-2 text-[15px] leading-relaxed text-stone-700">
              No. Gate + Gather runs on a flat monthly subscription instead of a per-sale commission. If you accept online card payments, standard payment-processing fees still apply — those go to the card network, not to Gate + Gather.
            </p>
          </div>
          <div>
            <p className="font-serif-heading text-lg text-[#20251F]">What happens after the 14-day Honesty Box trial?</p>
            <p className="mt-2 text-[15px] leading-relaxed text-stone-700">
              Nothing surprising. Your Gate pauses at the end of the trial. You choose whether to move up to a paid Gate plan or leave it closed — no automatic charges kick in without you deciding.
            </p>
          </div>
          <div>
            <p className="font-serif-heading text-lg text-[#20251F]">Can I close my Gate for the off-season?</p>
            <p className="mt-2 text-[15px] leading-relaxed text-stone-700">
              Yes. Growers who only sell in spring and autumn can close their Gate for the quiet months and reopen it when they've got stock. You shouldn't have to rebuild your storefront every season.
            </p>
          </div>
          <div>
            <p className="font-serif-heading text-lg text-[#20251F]">Which plan is right for me?</p>
            <p className="mt-2 text-[15px] leading-relaxed text-stone-700">
              If you're testing the water with a plant or two, start with the Honesty Box. If you already sell regularly through Instagram, Facebook, markets or word of mouth, the Gate plan is the better fit — it gives you the full storefront, online payment and stock tools your customers expect. Gate+ is on the roadmap for larger catalogues, staff access and printable order sheets.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#DED5C3] py-10 mt-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 flex-wrap">
          <Link to="/" data-testid="pricing-footer-brand" className="flex items-center gap-2 group">
            <GMark size={28} className="rounded-md shrink-0" />
            <span className="font-serif-heading text-base tracking-tight">Gate<span className="brand-plus"> + </span>Gather</span>
          </Link>
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/for-growers" data-testid="pricing-footer-growers" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">For growers</Link>
            <Link to="/faq" data-testid="pricing-footer-faq" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">FAQ</Link>
            <Link to="/policy/seller-policy" data-testid="pricing-footer-seller-policy" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Seller Policy</Link>
            <Link to="/policy/privacy" data-testid="pricing-footer-privacy" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Privacy</Link>
            <Link to="/policy/terms" data-testid="pricing-footer-terms" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Terms</Link>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <FooterMeta />
        </div>
      </footer>
    </div>
  );
}
